/*
 * Copyright 2024 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  PageBlueprint,
  configApiRef,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import {
  compatWrapper,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';
import { Suspense, lazy } from 'react';

import { FeedbackAPI, feedbackApiRef } from './api';
import { entityRootRouteRef, rootRouteRef, viewDocsRouteRef } from './routes';

const feedbackApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: feedbackApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          configApi: configApiRef,
          identityApi: identityApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, configApi, identityApi, fetchApi }) =>
          new FeedbackAPI({ discoveryApi, configApi, identityApi, fetchApi }),
      }),
    ),
});

const feedbackPage = PageBlueprint.make({
  params: {
    path: '/feedback',
    title: 'Feedback',
    icon: <TextsmsOutlinedIcon />,
    routeRef: convertLegacyRouteRef(rootRouteRef),
    loader: () =>
      import('./components/GlobalFeedbackPage').then(m =>
        compatWrapper(<m.GlobalFeedbackPage />),
      ),
  },
});

const LazyGlobalFeedbackComponent = lazy(() =>
  import('./components/GlobalFeedbackComponent').then(m => ({
    default: m.GlobalFeedbackComponent,
  })),
);

/**
 * Floating feedback widget rendered at the app root on every page.
 */
const feedbackGlobalComponent = AppRootElementBlueprint.make({
  name: 'global-feedback',
  params: {
    element: compatWrapper(
      <Suspense fallback={null}>
        <LazyGlobalFeedbackComponent />
      </Suspense>,
    ),
  },
});

/**
 * The Backstage new-frontend-system version of the feedback plugin.
 *
 * @public
 */
export default createFrontendPlugin({
  pluginId: 'feedback',
  routes: {
    root: convertLegacyRouteRef(rootRouteRef),
    entityRoot: convertLegacyRouteRef(entityRootRouteRef),
  },
  externalRoutes: {
    viewDocs: convertLegacyRouteRef(viewDocsRouteRef),
  },
  extensions: [feedbackApi, feedbackPage, feedbackGlobalComponent],
});
